// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { ButtonPresentationProps } from "@keller/MobiusOverrides/Button";
import { CartTotalDisplayStyles } from "@keller/Overrides/Components/CartTotalDisplay";
import { fontConfig } from "@keller/XDL/PreStyle/typography";
import { color, mergeWithCss, xScreen } from "@keller/XDL/Utilities";
import { css } from "styled-components";

export const cartTotalDisplayStylesFontStyles: { label?: TypographyProps; value?: TypographyProps } = {
    label: {
        size: 13,
        weight: "normal",
    },
    value: {
        size: 13,
        weight: "bold",
        css: css`
            margin-left: auto;
        `,
    },
};

export interface GridItemWithText {
    container?: GridItemProps;
    text?: TypographyPresentationProps;
}

export interface CartSummarySharedStyles {
    container?: GridContainerProps;
    labelGridItem?: GridItemProps;
    valueGridItem?: GridItemProps;
    label?: TypographyProps;
    value?: TypographyProps;
    totalLabel?: TypographyProps;
    totalValue?: TypographyProps;
    loadingSpinner?: LoadingSpinnerProps;
    titleItem?: GridItemWithText;
    taxAndShippingMessageItem?: GridItemWithText;
    itemsCountItem?: GridItemWithText;
    topSeparator?: GridItemProps;
    bottomSeparator?: GridItemProps;
    // XNG-Changes
    totalLabelGridItem?: GridItemProps;
    totalValueGridItem?: GridItemProps;
}

const cartSummarySharedStyles: CartSummarySharedStyles = {
    container: {
        gap: 0,
        css: css`
            background: ${color("common.white")};
            &&&& {
                max-width: 450px;
            }
        `,
        offsetCss: css`
            box-shadow: 0 2px 0 0 ${getColor("common.ui5")};
            border: 1px solid ${getColor("common.ui5")};
        `,
    },
    labelGridItem: {
        width: 6,
        css: css`
            padding-left: 17px;
            padding-bottom: 12px;
            align-items: center;
            @media print {
                padding: 4px 8px;
            }
        `,
    },
    valueGridItem: {
        width: 6,
        css: css`
            padding-right: 17px;
            padding-bottom: 12px;
            align-items: center;
            text-align: right;
            @media print {
                padding: 2px 8px;
            }
        `,
    },
    totalLabelGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            padding-left: 17px;
            padding-bottom: 12px;
            align-items: center;
            text-align: left;
            @media print {
                padding: 8px;
                flex-basis: 50%;
                max-width: 50%;
            }
        `,
    },
    totalValueGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            padding-right: 17px;
            padding-bottom: 12px;
            align-items: center;
            text-align: left;
            ${xScreen("lte", "sm")} {
                padding-left: 17px;
                padding-right: 0;
            }
            @media print {
                text-align: right;
                padding: 8px;
                flex-basis: 50%;
                max-width: 50%;
            }
        `,
    },
    label: {
        ...mergeWithCss(cartTotalDisplayStylesFontStyles.label, {
            css: css`
                @media print {
                    font-size: 12px;
                    font-weight: ${fontConfig.weight.bold};
                    line-height: 18px;
                }
            `

        })
    },
    value: {
        ...mergeWithCss(cartTotalDisplayStylesFontStyles.value, {
            css: css`
                @media print {
                    font-size: 12px;
                    font-weight: ${fontConfig.weight.bold};
                    line-height: 18px;
                }
            `

        })
    },
    totalLabel: {
        size: 18,
        weight: "bold",
        css: css`
            @media print {
                font-size: 12px;
            }
        `
    },
    totalValue: {
        size: 24,
        weight: 900,
        css: css`
            margin-left: auto;
            ${xScreen("lte", "sm")} {
                margin-left: initial;
            }
            @media print {
                font-size: 12px;
                font-weight: ${fontConfig.weight.bold};
            }
        `,
    },
    loadingSpinner: {
        size: 15,
        css: css`
            margin-top: auto;
            margin-bottom: auto;
            margin-left: auto;
        `,
    },
    titleItem: {
        container: {
            width: 12,
            css: css`
                padding: 14px 17px;
                background: ${getColor("common.ui12")};
                border-bottom: 1px solid ${getColor("common.ui5")};
                @media print {
                    background: none;
                    padding: 8px;
                }
            `,
        },
        text: {
            size: 15,
            lineHeight: "19px",
            weight: "bold",
            css: css`
                @media print {
                    font-size: 12px;
                    line-height: 18px;
                }
            `
        },
    },
    itemsCountItem: {
        container: {
            width: 12,
            css: css`
                padding: 12px 17px;
                @media print {
                    padding: 8px;
                }
            `,
        },
        text: {
            size: 14,
            lineHeight: "19px",
            weight: "bold",
            css: css`
                @media print {
                    font-size: 12px;
                    line-height: 18px;
                }
            `
        },
    },
    taxAndShippingMessageItem: {
        container: {
            width: 12,
            css: css`
                padding: 0 17px 12px;
            `,
        },
        text: {
            size: 14,
            lineHeight: "24px",
            css: css`
                padding: 12px;
                display: block;
                width: 100%;
                background: ${color("common.ui20")};
            `,
        },
    },
    topSeparator: {
        width: 12,
        css: css`
            &&& {
                display: none;
                & > div {
                    width: 100%;
                    height: 1px;
                    background: ${color("common.ui5")};
                    margin: 0 15px 5px;

                    @media print {
                        width: calc(100% - 16px);
                        margin-left: 8px;
                    }
                }
                @media print {
                    display: block;
                }
            }
        `,
    },
    bottomSeparator: {
        width: 12,
        css: css`
            &&& {
                display: none;
                & > div {
                    width: 100%;
                    height: 1px;
                    background: ${color("common.ui5")};
                    margin: 0 15px;

                    @media print {
                        width: calc(100% - 16px);
                        margin-left: 8px;
                    }
                }
                @media print {
                    display: block;
                }
            }
        `,
    },
};

export default cartSummarySharedStyles;
